import React, { useState, useCallback, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Tabs,
  List,
  Tag,
  Drawer,
  Row,
  Col,
  Alert,
  Divider,
  Tree,
  Tooltip,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CopyOutlined,
  SettingOutlined,
  NodeIndexOutlined,
  BranchesOutlined,
  ClockCircleOutlined,
  UserOutlined,
  MailOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import type {
  WorkflowTemplate,
  WorkflowNode,
  WorkflowConnection,
  NodeType,
  ApprovalFlow,
  ApprovalStep,
  Approver,
  ApproverType,
  DocumentType,
  ApprovalFlowType,
} from '../../types';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

interface WorkflowBuilderProps {
  workflows: ApprovalFlow[];
  onWorkflowCreate: (workflow: Partial<ApprovalFlow>) => void;
  onWorkflowUpdate: (id: string, workflow: Partial<ApprovalFlow>) => void;
  onWorkflowDelete: (id: string) => void;
  onWorkflowActivate: (id: string) => void;
  onWorkflowDeactivate: (id: string) => void;
}

export const WorkflowBuilder: React.FC<WorkflowBuilderProps> = ({
  workflows,
  onWorkflowCreate,
  onWorkflowUpdate,
  onWorkflowDelete,
  onWorkflowActivate,
  onWorkflowDeactivate,
}) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState<ApprovalFlow | null>(null);
  const [workflowModalVisible, setWorkflowModalVisible] = useState(false);
  const [stepModalVisible, setStepModalVisible] = useState(false);
  const [nodeConfigDrawerVisible, setNodeConfigDrawerVisible] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<ApprovalFlow | null>(null);
  const [editingStep, setEditingStep] = useState<ApprovalStep | null>(null);
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null);
  const [form] = Form.useForm();
  const [stepForm] = Form.useForm();
  const [nodeForm] = Form.useForm();
  const canvasRef = useRef<HTMLDivElement>(null);

  // Mock data for demonstration
  const mockUsers = [
    { id: '1', name: 'John Doe', role: 'Manager', department: 'IT' },
    { id: '2', name: 'Jane Smith', role: 'Director', department: 'Finance' },
    { id: '3', name: 'Mike Johnson', role: 'VP', department: 'Operations' },
  ];

  const mockRoles = [
    { id: '1', name: 'Department Manager' },
    { id: '2', name: 'Finance Director' },
    { id: '3', name: 'Executive VP' },
  ];

  const mockDepartments = [
    { id: '1', name: 'IT' },
    { id: '2', name: 'Finance' },
    { id: '3', name: 'Operations' },
    { id: '4', name: 'HR' },
  ];

  const nodeTypes: { type: NodeType; label: string; icon: React.ReactNode; color: string }[] = [
    { type: 'start', label: 'Start', icon: <PlayCircleOutlined />, color: '#52c41a' },
    { type: 'approval', label: 'Approval', icon: <CheckCircleOutlined />, color: '#1890ff' },
    { type: 'condition', label: 'Condition', icon: <BranchesOutlined />, color: '#faad14' },
    { type: 'notification', label: 'Notification', icon: <MailOutlined />, color: '#722ed1' },
    { type: 'delay', label: 'Delay', icon: <ClockCircleOutlined />, color: '#13c2c2' },
    { type: 'action', label: 'Action', icon: <ApiOutlined />, color: '#eb2f96' },
    { type: 'end', label: 'End', icon: <CloseCircleOutlined />, color: '#ff4d4f' },
  ];

  const workflowColumns = [
    {
      title: 'Workflow Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: ApprovalFlow) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.description}</Text>
        </div>
      ),
    },
    {
      title: 'Document Type',
      dataIndex: 'documentType',
      key: 'documentType',
      render: (type: DocumentType) => (
        <Tag>{type.replace('_', ' ').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: ApprovalFlowType) => (
        <Tag color="blue">{type.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Steps',
      dataIndex: 'steps',
      key: 'steps',
      render: (steps: ApprovalStep[]) => steps?.length || 0,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: ApprovalFlow) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditWorkflow(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => handleCopyWorkflow(record)}
          >
            Copy
          </Button>
          <Button
            type="link"
            icon={isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => handleToggleWorkflow(record)}
          >
            {record.isActive ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteWorkflow(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const handleEditWorkflow = (workflow: ApprovalFlow) => {
    setEditingWorkflow(workflow);
    setSelectedWorkflow(workflow);
    form.setFieldsValue(workflow);
    setWorkflowModalVisible(true);
  };

  const handleCopyWorkflow = (workflow: ApprovalFlow) => {
    const copiedWorkflow = {
      ...workflow,
      name: `${workflow.name} (Copy)`,
      isActive: false,
    };
    delete (copiedWorkflow as any).id;
    onWorkflowCreate(copiedWorkflow);
    message.success('Workflow copied successfully');
  };

  const handleToggleWorkflow = (workflow: ApprovalFlow) => {
    if (workflow.isActive) {
      onWorkflowDeactivate(workflow.id);
    } else {
      onWorkflowActivate(workflow.id);
    }
  };

  const handleDeleteWorkflow = (id: string) => {
    Modal.confirm({
      title: 'Delete Workflow',
      content: 'Are you sure you want to delete this workflow? This action cannot be undone.',
      onOk: () => onWorkflowDelete(id),
    });
  };

  const handleWorkflowSubmit = async (values: any) => {
    try {
      if (editingWorkflow) {
        onWorkflowUpdate(editingWorkflow.id, values);
      } else {
        onWorkflowCreate(values);
      }
      setWorkflowModalVisible(false);
      setEditingWorkflow(null);
      form.resetFields();
      message.success(`Workflow ${editingWorkflow ? 'updated' : 'created'} successfully`);
    } catch (error) {
      message.error('Failed to save workflow');
    }
  };

  const handleAddStep = () => {
    setEditingStep(null);
    stepForm.resetFields();
    setStepModalVisible(true);
  };

  const handleEditStep = (step: ApprovalStep) => {
    setEditingStep(step);
    stepForm.setFieldsValue(step);
    setStepModalVisible(true);
  };

  const handleStepSubmit = async (values: any) => {
    try {
      if (!selectedWorkflow) return;

      const newStep: ApprovalStep = {
        ...values,
        id: editingStep?.id || Date.now().toString(),
        stepNumber: editingStep?.stepNumber || (selectedWorkflow.steps?.length || 0) + 1,
        escalationRules: [],
        isActive: true,
      };

      const updatedSteps = editingStep
        ? selectedWorkflow.steps?.map(s => s.id === editingStep.id ? newStep : s) || []
        : [...(selectedWorkflow.steps || []), newStep];

      const updatedWorkflow = {
        ...selectedWorkflow,
        steps: updatedSteps,
      };

      setSelectedWorkflow(updatedWorkflow);
      onWorkflowUpdate(selectedWorkflow.id, updatedWorkflow);
      setStepModalVisible(false);
      setEditingStep(null);
      stepForm.resetFields();
      message.success(`Step ${editingStep ? 'updated' : 'added'} successfully`);
    } catch (error) {
      message.error('Failed to save step');
    }
  };

  const handleDeleteStep = (stepId: string) => {
    if (!selectedWorkflow) return;

    Modal.confirm({
      title: 'Delete Step',
      content: 'Are you sure you want to delete this step?',
      onOk: () => {
        const updatedSteps = selectedWorkflow.steps?.filter(s => s.id !== stepId) || [];
        const updatedWorkflow = {
          ...selectedWorkflow,
          steps: updatedSteps,
        };
        setSelectedWorkflow(updatedWorkflow);
        onWorkflowUpdate(selectedWorkflow.id, updatedWorkflow);
        message.success('Step deleted successfully');
      },
    });
  };

  const renderWorkflowCanvas = () => {
    if (!selectedWorkflow) {
      return (
        <div style={{ 
          height: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          border: '2px dashed #d9d9d9',
          borderRadius: '8px',
        }}>
          <div style={{ textAlign: 'center' }}>
            <NodeIndexOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
            <div style={{ marginTop: '16px', color: '#999' }}>
              Select a workflow to view its design
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ 
        height: '400px', 
        border: '1px solid #d9d9d9',
        borderRadius: '8px',
        padding: '16px',
        position: 'relative',
        overflow: 'auto',
      }}>
        {/* Workflow Steps Visualization */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          {selectedWorkflow.steps?.map((step, index) => (
            <React.Fragment key={step.id}>
              <div
                style={{
                  padding: '12px 16px',
                  border: '2px solid #1890ff',
                  borderRadius: '8px',
                  backgroundColor: '#f0f9ff',
                  cursor: 'pointer',
                  minWidth: '120px',
                  textAlign: 'center',
                }}
                onClick={() => handleEditStep(step)}
              >
                <div style={{ fontWeight: 500 }}>{step.name}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {step.approvers?.length || 0} approvers
                </div>
                <Tag size="small" color="blue">{step.type}</Tag>
              </div>
              {index < (selectedWorkflow.steps?.length || 0) - 1 && (
                <div style={{ fontSize: '20px', color: '#1890ff' }}>→</div>
              )}
            </React.Fragment>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddStep}
            style={{ minHeight: '60px' }}
          >
            Add Step
          </Button>
        </div>
      </div>
    );
  };

  const renderNodePalette = () => (
    <Card title="Node Palette" size="small">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
        {nodeTypes.map(nodeType => (
          <div
            key={nodeType.type}
            style={{
              padding: '8px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              cursor: 'pointer',
              textAlign: 'center',
              backgroundColor: '#fafafa',
            }}
            onClick={() => {
              // Handle node drag start
              message.info(`Selected ${nodeType.label} node`);
            }}
          >
            <div style={{ color: nodeType.color, fontSize: '16px' }}>
              {nodeType.icon}
            </div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              {nodeType.label}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Workflow Builder</Title>
          <Text type="secondary">Create and manage approval workflows</Text>
        </div>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setWorkflowModalVisible(true)}>
            Create Workflow
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        {/* Workflow List */}
        <Col xs={24} lg={8}>
          <Card title="Workflows" size="small">
            <List
              dataSource={workflows}
              renderItem={(workflow) => (
                <List.Item
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedWorkflow?.id === workflow.id ? '#f0f9ff' : 'transparent',
                  }}
                  onClick={() => setSelectedWorkflow(workflow)}
                  actions={[
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditWorkflow(workflow);
                      }}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={workflow.name}
                    description={
                      <div>
                        <Tag size="small">{workflow.documentType}</Tag>
                        <Tag size="small" color={workflow.isActive ? 'green' : 'red'}>
                          {workflow.isActive ? 'Active' : 'Inactive'}
                        </Tag>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Workflow Designer */}
        <Col xs={24} lg={16}>
          <Card
            title={selectedWorkflow ? `Workflow: ${selectedWorkflow.name}` : 'Workflow Designer'}
            extra={
              selectedWorkflow && (
                <Space>
                  <Button icon={<SaveOutlined />}>Save</Button>
                  <Button icon={<SettingOutlined />}>Settings</Button>
                </Space>
              )
            }
          >
            {renderWorkflowCanvas()}
          </Card>

          {/* Workflow Steps */}
          {selectedWorkflow && (
            <Card title="Workflow Steps" style={{ marginTop: '16px' }}>
              <List
                dataSource={selectedWorkflow.steps}
                renderItem={(step, index) => (
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditStep(step)}
                      />,
                      <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteStep(step.id)}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<div style={{ 
                        width: '32px', 
                        height: '32px', 
                        borderRadius: '50%', 
                        backgroundColor: '#1890ff',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                      }}>
                        {index + 1}
                      </div>}
                      title={step.name}
                      description={
                        <div>
                          <div>{step.description}</div>
                          <div style={{ marginTop: '4px' }}>
                            <Tag size="small">{step.type}</Tag>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {step.approvers?.length || 0} approvers
                            </Text>
                            {step.timeoutHours && (
                              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                                Timeout: {step.timeoutHours}h
                              </Text>
                            )}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddStep}
                style={{ width: '100%', marginTop: '16px' }}
              >
                Add Step
              </Button>
            </Card>
          )}
        </Col>
      </Row>

      {/* Workflow Modal */}
      <Modal
        title={editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}
        open={workflowModalVisible}
        onCancel={() => {
          setWorkflowModalVisible(false);
          setEditingWorkflow(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleWorkflowSubmit}
        >
          <Form.Item
            name="name"
            label="Workflow Name"
            rules={[{ required: true, message: 'Please enter workflow name' }]}
          >
            <Input placeholder="e.g., Purchase Order Approval" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Describe the workflow purpose" />
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="documentType"
                label="Document Type"
                rules={[{ required: true, message: 'Please select document type' }]}
              >
                <Select placeholder="Select document type">
                  <Option value="purchase_requisition">Purchase Requisition</Option>
                  <Option value="purchase_order">Purchase Order</Option>
                  <Option value="vendor_invoice">Vendor Invoice</Option>
                  <Option value="payment_request">Payment Request</Option>
                  <Option value="contract">Contract</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="type"
                label="Workflow Type"
                rules={[{ required: true, message: 'Please select workflow type' }]}
              >
                <Select placeholder="Select workflow type">
                  <Option value="sequential">Sequential</Option>
                  <Option value="parallel">Parallel</Option>
                  <Option value="conditional">Conditional</Option>
                  <Option value="hybrid">Hybrid</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setWorkflowModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingWorkflow ? 'Update' : 'Create'} Workflow
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Step Modal */}
      <Modal
        title={editingStep ? 'Edit Step' : 'Add Step'}
        open={stepModalVisible}
        onCancel={() => {
          setStepModalVisible(false);
          setEditingStep(null);
          stepForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={stepForm}
          layout="vertical"
          onFinish={handleStepSubmit}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Step Name"
                rules={[{ required: true, message: 'Please enter step name' }]}
              >
                <Input placeholder="e.g., Manager Approval" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="type"
                label="Step Type"
                rules={[{ required: true, message: 'Please select step type' }]}
              >
                <Select placeholder="Select step type">
                  <Option value="single_approver">Single Approver</Option>
                  <Option value="multiple_approvers">Multiple Approvers</Option>
                  <Option value="any_one">Any One</Option>
                  <Option value="majority">Majority</Option>
                  <Option value="unanimous">Unanimous</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={2} placeholder="Describe the step" />
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="requiredApprovals"
                label="Required Approvals"
                initialValue={1}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="timeoutHours"
                label="Timeout (Hours)"
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Approvers</Divider>

          <Alert
            message="Approver Configuration"
            description="Configure who can approve at this step. You can add multiple approvers with different types."
            type="info"
            style={{ marginBottom: '16px' }}
          />

          {/* Approvers would be managed with a dynamic form list */}
          <div style={{ marginBottom: '16px' }}>
            <Button type="dashed" icon={<PlusOutlined />} style={{ width: '100%' }}>
              Add Approver
            </Button>
          </div>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setStepModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingStep ? 'Update' : 'Add'} Step
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
}; 